import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import { apiRequest } from "../../utils/apiUtils";

// Use centralized API configuration
const API_URL = `${BASE_API_URL}/auth`;

// Safely get user from localStorage
const getSafeUser = () => {
  try {
    // IMPORTANT: Check if this is a corporate session
    // Corporate staff should NOT have user in Redux state
    const corporateToken = localStorage.getItem("corporateToken");
    if (corporateToken) {
      // This is a corporate session, don't load personal user
      console.log("Corporate session detected, skipping personal user load");
      return null;
    }

    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.warn("Invalid user data in localStorage, clearing corrupted data:", error);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    return null;
  }
};

const user = getSafeUser();

const initialState = {
  user: user ? user : null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

// Register user
export const register = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await axios.post(`${API_URL}/register`, userData);

      if (response.data && response.data.success) {
        localStorage.setItem("user", JSON.stringify(response.data));

        // Also store token separately for easier access in API calls
        if (response.data.token) {
          localStorage.setItem("token", response.data.token);
        }
      }

      return response.data;
    } catch (error) {
      // Improved error message extraction
      let message;

      if (error.response && error.response.data) {
        // If API returns a message property
        if (error.response.data.message) {
          message = error.response.data.message;
        }
        // Some APIs might return the error directly in the data
        else if (typeof error.response.data === "string") {
          message = error.response.data;
        }
        // Or the error might be in an 'error' property
        else if (error.response.data.error) {
          message = error.response.data.error;
        }
      }

      // Fallback to default error handling if we couldn't extract a message
      if (!message) {
        message = error.message || "An error occurred during registration";
      }

      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Login user
export const login = createAsyncThunk(
  "auth/login",
  async (userData, thunkAPI) => {
    try {
      const response = await axios.post(`${API_URL}/login`, userData);

      if (response.data && response.data.success) {
        // Check if this is a corporate staff login
        const corporateToken = response.data.corporateToken;
        if (corporateToken) {
          // Corporate staff login - store corporate tokens and member data
          localStorage.setItem("corporateToken", corporateToken);

          // Also store regular token if different
          if (response.data.token && response.data.token !== corporateToken) {
            localStorage.setItem("token", response.data.token);
          }

          // Store corporate member data
          const member = response.data.data || {};
          localStorage.setItem("corporateMember", JSON.stringify(member));

          // IMPORTANT: Clear personal user session for corporate staff
          localStorage.removeItem("user");

          // Dispatch event to notify Header of corporate session change
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event("corporateSessionChanged"));
          }
        } else {
          // Regular user login - store personal user data
          localStorage.setItem("user", JSON.stringify(response.data));

          // Also store token separately for easier access in API calls
          if (response.data.token) {
            localStorage.setItem("token", response.data.token);
          }

          // Clear corporate session if switching from corporate to personal
          localStorage.removeItem("corporateToken");
          localStorage.removeItem("corporateMember");
        }
      }

      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Request OTP
export const requestOtp = createAsyncThunk(
  "auth/requestOtp",
  async (phoneData, thunkAPI) => {
    try {
      // Support role parameter for role-based OTP requests
      // Default to "user" if no role is provided
      const requestPayload = {
        ...phoneData,
        role: phoneData.role || "user",
      };
      const response = await axios.post(`${API_URL}/request-otp`, requestPayload);
      return response.data;
    } catch (error) {
      // Preserve error code and field info for better error handling
      const errorData = error.response?.data || {};
      const errorPayload = {
        message: errorData.message || error.message || "An error occurred",
        code: errorData.code || null,
        field: errorData.field || null,
      };

      return thunkAPI.rejectWithValue(errorPayload);
    }
  },
);

// Verify OTP
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (otpData, thunkAPI) => {
    try {
      // Support role parameter for role-based OTP verification
      // Default to "user" if no role is provided
      const requestPayload = {
        ...otpData,
        role: otpData.role || "user",
      };
      const response = await axios.post(`${API_URL}/verify-otp`, requestPayload);

      if (response.data && response.data.success) {
        // Check if this is a corporate staff login
        const corporateToken = response.data.corporateToken;
        if (corporateToken) {
          // Corporate staff login - store corporate tokens and member data
          localStorage.setItem("corporateToken", corporateToken);

          // Also store regular token if different
          if (response.data.token && response.data.token !== corporateToken) {
            localStorage.setItem("token", response.data.token);
          }

          // Store corporate member data
          const member = response.data.data || {};
          localStorage.setItem("corporateMember", JSON.stringify(member));

          // IMPORTANT: Clear personal user session for corporate staff
          localStorage.removeItem("user");

          // Dispatch event to notify Header of corporate session change
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event("corporateSessionChanged"));
          }
        } else {
          // Regular user login - store personal user data
          localStorage.setItem("user", JSON.stringify(response.data));

          // Also store token separately for easier access in API calls
          if (response.data.token) {
            localStorage.setItem("token", response.data.token);
          }

          // Clear corporate session if switching from corporate to personal
          localStorage.removeItem("corporateToken");
          localStorage.removeItem("corporateMember");
        }
      }

      return response.data;
    } catch (error) {
      // Preserve error code and field info for better error handling
      const errorData = error.response?.data || {};
      const errorPayload = {
        message: errorData.message || error.message || "An error occurred",
        code: errorData.code || null,
        field: errorData.field || null,
      };

      return thunkAPI.rejectWithValue(errorPayload);
    }
  },
);

// Logout user
export const logout = createAsyncThunk("auth/logout", async () => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
});

// Update user profile
export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (userData, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.put(
        `${API_URL}/updatedetails`,
        userData,
        config,
      );

      // Update user in localStorage
      const updatedUser = {
        ...thunkAPI.getState().auth.user,
        user: response.data.data,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = "";
    },
    loginSuccess: (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      state.message = "";
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = "";
        // For corporate staff login, don't set user in Redux - use corporate session instead
        const isCorporateLogin = action.payload?.corporateToken || localStorage.getItem("corporateToken");
        state.user = isCorporateLogin ? null : action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        state.user = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Replace the user object reference so React components depending on `user` detect changes
        const current = state.user || {};
        const updatedUser = {
          ...current,
          user: action.payload.data,
        };
        state.user = updatedUser;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(requestOtp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(requestOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false; // Don't set success for OTP request
        state.message = action.payload.message;
      })
      .addCase(requestOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        // Extract message from payload object or use as string
        state.message = typeof action.payload === "object" ? action.payload.message : action.payload;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = "";
        // For corporate staff login, don't set user in Redux - use corporate session instead
        const isCorporateLogin = action.payload?.corporateToken || localStorage.getItem("corporateToken");
        state.user = isCorporateLogin ? null : action.payload;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        // Extract message from payload object or use as string
        state.message = typeof action.payload === "object" ? action.payload.message : action.payload;
        state.user = null;
      });
  },
});

export const { reset, loginSuccess } = authSlice.actions;
export default authSlice.reducer;
